import React, { useCallback, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { Breadcrumb, Input, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/lib/constants';
import { EMPTY_MESSAGES } from '@/shared/lib/constants/messages';
import { formatDateTime } from '@/shared/lib/format';
import { highlightText } from '@/shared/lib/text';
import { formatBytes } from '@/shared/lib/utils/number';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { Icon } from '@/shared/ui/Icon';
import { TableEmpty } from '@/shared/ui/TableEmpty';

import type { FileItem } from '@/features/asset-detail';
import { useContainerFiles } from '@/features/asset-detail';

import styles from './FilesTab.module.css';

type FilesTabProps = {
  entityId: string;
  containerFqn: string;
};

const FilesTabComponent: React.FC<FilesTabProps> = ({ entityId: _entityId, containerFqn }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPath, setCurrentPath] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data: filesData, isLoading, isError, error, refetch } = useContainerFiles(containerFqn, currentPath);

  const breadcrumbItems = useMemo(() => {
    const pathParts = currentPath.split('/').filter(Boolean);

    if (pathParts.length === 0) {
      return [];
    }

    const items = [
      {
        title: (
          <a onClick={() => setCurrentPath('')} className={styles.breadcrumbItem}>
            <Icon name="home" size={14} />
            <span>Home</span>
          </a>
        ),
      },
    ];

    pathParts.forEach((part, index) => {
      const path = pathParts.slice(0, index + 1).join('/');
      items.push({
        title: (
          <a onClick={() => setCurrentPath(path)} className={styles.breadcrumbItem}>
            <EllipsisText text={part} className={styles.breadcrumbText} />
          </a>
        ),
      });
    });

    return items;
  }, [currentPath]);

  const filteredFiles = useMemo(() => {
    if (!filesData?.files) return [];
    if (!searchQuery || searchQuery.length === 0) return filesData.files;

    const lowerQuery = searchQuery.toLowerCase();
    return filesData.files.filter(
      (file) =>
        file.name.toLowerCase().includes(lowerQuery) ||
        file.path.toLowerCase().includes(lowerQuery) ||
        file.mimeType?.toLowerCase().includes(lowerQuery)
    );
  }, [filesData, searchQuery]);

  const handleRowClick = (record: FileItem) => {
    if (record.type === 'folder') {
      const newPath = currentPath ? `${currentPath}/${record.name}` : record.name;
      setCurrentPath(newPath);
      setSearchQuery('');
    }
  };

  const columns: ColumnsType<FileItem> = useMemo(
    () => [
      {
        title: '이름',
        dataIndex: 'name',
        key: 'name',
        width: 240,
        render: (name: string, record: FileItem) => (
          <div className={styles.nameCell}>
            {record.type === 'folder' ? (
              <Icon name="folder" className={styles.folderIcon} />
            ) : (
              <Icon name="file" className={styles.fileIcon} />
            )}
            <span className={record.type === 'folder' ? styles.folderName : styles.fileName}>
              {highlightText(name, searchQuery, { className: 'highlight' })}
            </span>
          </div>
        ),
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: '유형',
        dataIndex: 'type',
        key: 'type',
        width: 150,
        render: (type: string, record: FileItem) => {
          if (type === 'folder') return '폴더';
          const label = record.mimeType || '파일';
          return highlightText(label, searchQuery, { className: 'highlight' });
        },
        filters: [
          { text: '폴더', value: 'folder' },
          { text: '파일', value: 'file' },
        ],
        onFilter: (value, record) => record.type === value,
      },
      {
        title: '크기',
        dataIndex: 'size',
        key: 'size',
        width: 150,
        render: (size?: number) => (size !== undefined ? formatBytes(size) : '-'),
        sorter: (a, b) => (a.size || 0) - (b.size || 0),
      },
      {
        title: '수정일',
        dataIndex: 'lastModified',
        key: 'lastModified',
        width: 150,
        render: (date?: string) => (date ? formatDateTime(date) : '-'),
        sorter: (a, b) => {
          if (!a.lastModified) return 1;
          if (!b.lastModified) return -1;
          return dayjs(a.lastModified).valueOf() - dayjs(b.lastModified).valueOf();
        },
      },
      {
        title: '경로',
        dataIndex: 'path',
        key: 'path',
        render: (path: string) => (
          <EllipsisText text={path} tooltipPlacement="topLeft">
            {highlightText(path, searchQuery, { className: 'highlight' })}
          </EllipsisText>
        ),
      },
    ],
    [searchQuery]
  );

  const pageParam = searchParams.get('page');
  const pageSizeParam = searchParams.get('pageSize');
  const initialPage = pageParam ? parseInt(pageParam, 10) : 1;
  const initialPageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : DEFAULT_PAGE_SIZE;

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', String(page));
      newSearchParams.set('pageSize', String(pageSize));
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const { paginationConfig, scrollConfig } = usePagination({
    dataLength: filteredFiles.length,
    viewportOffset: 615,
    initialPage,
    initialPageSize,
    onPageChange: handlePageChange,
  });

  if (isError) {
    return (
      <ErrorFallback
        error={error}
        description="파일 정보를 불러오지 못했습니다. 다시 시도해 주세요."
        resetErrorBoundary={refetch}
      />
    );
  }

  return (
    <div className={styles.filesTab}>
      <div className={styles.header}>
        <Breadcrumb items={breadcrumbItems} />
        <Input
          placeholder="파일 검색..."
          prefix={<Icon name="search" />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
          allowClear
        />
      </div>
      <Table
        columns={columns}
        dataSource={filteredFiles}
        rowKey={(record) => record.path}
        loading={isLoading}
        pagination={paginationConfig}
        scroll={{ x: filteredFiles.length > 0 ? 'max-content' : undefined, ...scrollConfig }}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: record.type === 'folder' ? 'pointer' : 'default' },
        })}
        locale={{
          emptyText: (
            <TableEmpty
              searchQuery={searchQuery}
              description={searchQuery ? undefined : EMPTY_MESSAGES.NO_TABLE_ITEMS}
            />
          ),
        }}
        className={styles.filesTable}
      />
    </div>
  );
};

export const FilesTab = React.memo(FilesTabComponent);
